import React, { FC, useEffect, useMemo, useState } from 'react';
import { useLazyGetAllCustomersQuery } from 'containers/invoices/api';
import { useLazyGetMerchantsByTeamIdReceiptsQuery } from 'containers/receipts/api';
import DropdownWithSearch from 'components/dropdown-with-search/DropdownWithSearch';
import { useAppSelector } from 'hooks';
import { JobLeadSourceTypes, JobStatus } from '../constant';
import CustomDateRangePicker from 'components/react-date-range-picker/ReactDateRangePicker';
import CustomAmountFilter, { AmountFilter } from 'components/custom-amount-filter/CustomAmountFilter';
import { AmountFilterOperation } from 'constants/common';
import JobMoreFilter from './JobMoreFilter';
import DateUtils from 'utils/dateUtils';
import {
  calculateDateRangeLabel,
  getDateDefaultRangeForMaps,
  getDateRangeFromSchedulePreset,
  getSchedulePresetFromLabel,
  getTextForFilter
} from 'containers/dispatch/utils';

import { useLazyGetExternalCompaniesQuery } from 'containers/settings/external-company-settings/api';
import { CommissionStatus } from 'containers/settings/commission-settings/constants';
import { useLazyGetTagsQuery } from 'containers/tags/api';
import { selectCurrentTeamJobPermissions, selectMerchantCustomizations } from 'containers/app/appSlice';
import { JobStatusFilters } from '../utils';
import { AnytimeCalendarIcon, NotScheduledCalendarIcon } from 'assets/icons';
import { useLazyGetServiceAreasQuery } from 'containers/settings/service-areas/api';
import { useViewFilters } from '../hooks/useViewFilters';
import { SchedulePreset } from 'containers/dispatch/constants';
import { format } from 'date-fns';

interface JobsFilterProps {
  statuses?: string[];
  setStatuses?: (value: JobStatus[]) => void;
  currentTeamId: number;
  selectedCustomers: string[];
  setSelectedCustomers: (value: string[]) => void;
  selectedMerchants: string[];
  setSelectedMerchants: (value: string[]) => void;
  selectedTeamMembers?: string[];
  setSelectedTeamMembers?: (value: string[]) => void;
  leadSources: string[];
  setLeadSources: (value: string[]) => void;
  amountFilter: AmountFilter;
  setAmountFilter: (amountFilter: AmountFilter) => void;
  scheduleStartDate: string | null;
  setScheduleStartDate: (startDate: string | null) => void;
  scheduleEndDate: string | null;
  setScheduleEndDate: (endDate: string | null) => void;
  externalCompanies: string[];
  setExternalCompanies: (value: string[]) => void;
  selectedTags: string[];
  setSelectedTags: (value: string[]) => void;
  showStatusFilter: boolean;
  showTeamFilter: boolean;
  selectedServiceAreas: string[];
  setSelectedServiceAreas: (value: string[]) => void;
  onDateRangeChange?: (startDate: string | null, endDate: string | null) => void;
}

const JobsFilter: FC<JobsFilterProps> = ({
  statuses,
  setStatuses,
  currentTeamId,
  selectedCustomers,
  setSelectedCustomers,
  selectedMerchants,
  setSelectedMerchants,
  selectedTeamMembers,
  setSelectedTeamMembers,
  leadSources,
  setLeadSources,
  amountFilter,
  setAmountFilter,
  scheduleStartDate,
  setScheduleStartDate,
  scheduleEndDate,
  setScheduleEndDate,
  externalCompanies,
  setExternalCompanies,
  selectedTags,
  setSelectedTags,
  showStatusFilter = true,
  showTeamFilter = true,
  selectedServiceAreas,
  setSelectedServiceAreas,
  onDateRangeChange
}) => {
  const [getCustomersQuery, getCustomersQueryState] = useLazyGetAllCustomersQuery();
  const [getMerchantsQuery, getMerchantsQueryState] = useLazyGetMerchantsByTeamIdReceiptsQuery();
  const [getExternalCompaniesQuery, getExternalCompaniesState] = useLazyGetExternalCompaniesQuery();
  const [getTagsQuery, getTagsQueryState] = useLazyGetTagsQuery();
  const [getServiceAreasQuery, { data: serviceAreasData }] = useLazyGetServiceAreasQuery();

  const { data: getCustomersResponse } = getCustomersQueryState;
  const { data: getMerchantsResponse } = getMerchantsQueryState;
  const { data: companiesData } = getExternalCompaniesState;
  const { data: tagsData } = getTagsQueryState;
  const [statusesDraft, setStatusesDraft] = useState<string[]>([]);
  const [customerSearchString, setCustomerSearchString] = useState('');
  const [tagsSearchString, setTagsSearchString] = useState('');
  const [selectedCustomersDraft, setSelectedCustomersDraft] = useState<string[]>([]);
  const [selectedTagsDraft, setSelectedTagsDraft] = useState<string[]>([]);
  const [createdBySearchString, setCreatedBySearchString] = useState('');
  const [selectedMerchantsDraft, setSelectedMerchantsDraft] = useState<string[]>([]);
  const [assigneeSearchString, setAssigneeSearchString] = useState('');
  const [assigneesDraft, setAssigneesDraft] = useState<string[]>([]);
  const [leadSourcesDraft, setLeadSourcesDraft] = useState<string[]>([]);
  const [externalCompaniesDraft, setExternalCompaniesDraft] = useState<string[]>([]);
  const [serviceAreasDraft, setServiceAreasDraft] = useState<string[]>([]);
  const [serviceAreaSearch, setServiceAreaSearch] = useState('');
  const { updateCurrentViewFilters, currentViewFilters } = useViewFilters();
  const jobPermissions = useAppSelector(selectCurrentTeamJobPermissions);
  const { teamConfigs } = useAppSelector(selectMerchantCustomizations);

  const statusFilterList = JobStatusFilters(jobPermissions, teamConfigs);
  const { viewJobSource, editJobSource } = useAppSelector(selectCurrentTeamJobPermissions);

  const externalCompanyOptions = useMemo(
    () =>
      companiesData?.data?.map(company => ({
        id: company.id,
        value: company.id,
        label: company.companyName
      })) || [],
    [companiesData?.data]
  );

  const tagsOptions = useMemo(() => tagsData?.data?.tags || [], [tagsData?.data?.tags]);

  const serviceAreaOptions = useMemo(
    () => (serviceAreasData?.data ? serviceAreasData?.data?.map(area => ({ id: area.id, name: area.name })) : []),
    [serviceAreasData]
  );

  useEffect(() => {
    if (currentTeamId) {
      getCustomersQuery({ teamId: currentTeamId });
      getMerchantsQuery({ teamId: currentTeamId });
      getExternalCompaniesQuery({ teamId: currentTeamId.toString(), status: CommissionStatus.ACTIVE });
      getTagsQuery({ teamId: currentTeamId });
      getServiceAreasQuery({ teamId: currentTeamId.toString(), params: { sortBy: 'createdAt', sortOrder: 'ASC' } });
    }
  }, [currentTeamId]);

  useEffect(() => {
    setSelectedCustomersDraft(selectedCustomers);
  }, [selectedCustomers]);
  useEffect(() => {
    setSelectedTagsDraft(selectedTags);
  }, [selectedTags]);
  useEffect(() => {
    setSelectedMerchantsDraft(selectedMerchants);
  }, [selectedMerchants]);
  useEffect(() => {
    setAssigneesDraft(selectedTeamMembers || []);
  }, [selectedTeamMembers]);
  useEffect(() => {
    setLeadSourcesDraft(leadSources);
  }, [leadSources]);
  useEffect(() => {
    setExternalCompaniesDraft(externalCompanies);
  }, [externalCompanies]);
  useEffect(() => {
    setServiceAreasDraft(selectedServiceAreas);
  }, [selectedServiceAreas]);

  const handleStatusChange = (newStatuses: JobStatus[]) => {
    if (setStatuses) {
      setStatuses(newStatuses);
    }
    updateCurrentViewFilters({ ...currentViewFilters, status: newStatuses });
  };

  const handleTeamMembersChange = (newTeamMembers: string[]) => {
    if (setSelectedTeamMembers) {
      setSelectedTeamMembers(newTeamMembers);
    }
    updateCurrentViewFilters({ ...currentViewFilters, assignees: newTeamMembers });
  };

  const handleMerchantsChange = (newMerchants: string[]) => {
    setSelectedMerchants(newMerchants);
    updateCurrentViewFilters({ ...currentViewFilters, createdBy: newMerchants });
  };

  const handleLeadSourcesChange = (newLeadSources: string[]) => {
    setLeadSources(newLeadSources);
    updateCurrentViewFilters({ ...currentViewFilters, leadSource: newLeadSources });
  };

  const handleCustomersChange = (newCustomers: string[]) => {
    setSelectedCustomers(newCustomers);
    updateCurrentViewFilters({ ...currentViewFilters, customerIds: newCustomers });
  };

  const handleAmountFilterChange = (newAmountFilter: AmountFilter) => {
    setAmountFilter(newAmountFilter);
    updateCurrentViewFilters({ ...currentViewFilters, amount: newAmountFilter });
  };

  const handleExternalCompaniesChange = (newExternalCompanies: string[]) => {
    setExternalCompanies(newExternalCompanies);
    updateCurrentViewFilters({ ...currentViewFilters, externalCompany: newExternalCompanies });
  };

  const handleTagsChange = (newTags: string[]) => {
    setSelectedTags(newTags);
    updateCurrentViewFilters({ ...currentViewFilters, tags: newTags });
  };

  const handleServiceAreasChange = (newAreas: string[]) => {
    setSelectedServiceAreas(newAreas);
    updateCurrentViewFilters({ ...currentViewFilters, serviceAreaId: newAreas });
  };

  const setDateRange = (updatedStartDate: string | null, updatedEndDate: string | null) => {
    if (onDateRangeChange) {
      onDateRangeChange(updatedStartDate, updatedEndDate);
    } else {
      if (!updatedStartDate && !updatedEndDate) {
        updateCurrentViewFilters({
          ...currentViewFilters,
          scheduledFrom: null,
          scheduledTo: null,
          isToday: false,
          schedulePreset: null,
          isUnscheduled: true
        });
      } else {
        // Check if this matches a schedule preset
        const dateRangeLabel = calculateDateRangeLabel(updatedStartDate, updatedEndDate);
        const preset = getSchedulePresetFromLabel(dateRangeLabel);

        // Special handling for "Today" - set isToday instead of schedulePreset
        if (dateRangeLabel === 'Today') {
          updateCurrentViewFilters({
            ...currentViewFilters,
            scheduledFrom: updatedStartDate,
            scheduledTo: updatedEndDate,
            isUnscheduled: false,
            schedulePreset: null,
            isToday: true
          });
        } else {
          updateCurrentViewFilters({
            ...currentViewFilters,
            scheduledFrom: updatedStartDate,
            scheduledTo: updatedEndDate,
            isToday: false,
            isUnscheduled: false,
            schedulePreset: preset
          });
        }
      }
    }
  };

  const handleStartDateChange = (date: string | null) => {
    setScheduleStartDate(date);
  };

  const handleEndDateChange = (date: string | null) => {
    setScheduleEndDate(date);
  };

  const handleClearDateRange = () => {
    updateCurrentViewFilters({
      ...currentViewFilters,
      scheduledFrom: null,
      scheduledTo: null,
      isToday: false,
      isUnscheduled: false,
      schedulePreset: null
    });
  };

  const getCustomDateRangeText = () => {
    // If schedule preset is selected, show the preset label
    if (currentViewFilters.schedulePreset) {
      const presetLabels = {
        [SchedulePreset.TODAY]: 'Today',
        [SchedulePreset.NEXT_7_DAYS]: 'Next 7 days',
        [SchedulePreset.NEXT_14_DAYS]: 'Next 14 days',
        [SchedulePreset.THIS_WEEK]: 'This Week',
        [SchedulePreset.LAST_WEEK]: 'Last Week',
        [SchedulePreset.THIS_MONTH]: 'This Month',
        [SchedulePreset.LAST_MONTH]: 'Last Month',
        [SchedulePreset.THIS_YEAR]: 'This year',
        [SchedulePreset.LAST_YEAR]: 'Last year'
      };
      return presetLabels[currentViewFilters.schedulePreset as SchedulePreset] || '';
    }

    if (currentViewFilters.isToday) {
      return 'Today';
    }

    const customText = '';
    if (scheduleStartDate && !scheduleEndDate) {
      if (
        DateUtils.getDateInFormat({ date: new Date(scheduleStartDate), dateFormat: 'yyyy-MM-dd' }) ===
        DateUtils.getDateInFormat({ date: new Date(0), dateFormat: 'yyyy-MM-dd' })
      )
        return 'Any time';
    }

    if (currentViewFilters.isUnscheduled) return 'Unscheduled';
    return customText;
  };

  const unscheduledContent = (
    <div className="flex h-full w-full flex-col items-center justify-center text-center">
      <NotScheduledCalendarIcon className="mb-2 h-8 w-8 text-gray-500" />
      <span className="text-base font-medium">Not scheduled</span>
      <span className="text-sm text-primaryText">Jobs where schedule date and times are empty.</span>
    </div>
  );

  const anyTimeContent = (
    <div className="flex h-full w-full flex-col items-center justify-center text-center">
      <AnytimeCalendarIcon className="mb-2 h-8 w-8 text-gray-500" />
      <span className="text-base font-medium">Any time</span>
      <span className="text-sm text-primaryText">View all scheduled jobs</span>
    </div>
  );

  // Add useEffect to sync preset with actual dates
  useEffect(() => {
    if (currentViewFilters.schedulePreset) {
      const dateRange = getDateRangeFromSchedulePreset(currentViewFilters.schedulePreset as string);
      if (dateRange.startDate && dateRange.endDate) {
        const formattedStart = format(dateRange.startDate, 'yyyy-MM-dd');
        const formattedEnd = format(dateRange.endDate, 'yyyy-MM-dd');
        setScheduleStartDate(formattedStart);
        setScheduleEndDate(formattedEnd);
      }
    } else if (currentViewFilters.isToday) {
      const currentDate = format(new Date(), 'yyyy-MM-dd');
      setScheduleStartDate(currentDate);
      setScheduleEndDate(currentDate);
    }
  }, [currentViewFilters.schedulePreset, currentViewFilters.isToday, setScheduleStartDate, setScheduleEndDate]);

  return (
    <>
      <div className="flex w-full flex-wrap gap-3">
        <CustomDateRangePicker
          startDate={scheduleStartDate}
          setStartDate={handleStartDateChange}
          endDate={scheduleEndDate}
          setEndDate={handleEndDateChange}
          filterId="jobs-schedule-for-filter"
          allowFutureDate={true}
          customRanges={getDateDefaultRangeForMaps()}
          hasDefaultRanges={false}
          emptyStateContent={unscheduledContent}
          setDateRange={setDateRange}
          anytimeContent={anyTimeContent}
          placeholder="Scheduled for"
          customSecondaryText={getCustomDateRangeText()}
          onClear={handleClearDateRange}
        />
        {showStatusFilter && (
          <DropdownWithSearch
            filterId="jobs-status-filter"
            data={statusFilterList}
            selectedDraftArray={statusesDraft}
            setSelectedDraftArray={setStatusesDraft}
            selectedArray={statuses}
            setSelectedArray={handleStatusChange}
            dataKey="label"
            dataIdKey="value"
            buttonPrimaryText="Status"
            buttonSecondaryText={getTextForFilter(statusFilterList, statuses, 'value', 'label')}
            filterPlaceholderText="Filter by Status"
            allowSearch={false}
            filterPlaceholderStyle="font-semibold"
            searchInputStyle="placeholder:text-primaryText"
          />
        )}
        {showTeamFilter && (
          <DropdownWithSearch
            searchFilterId="jobs-members-search-field"
            filterId="jobs-members-filter"
            data={getMerchantsResponse || []}
            searchText={assigneeSearchString}
            setSearchText={setAssigneeSearchString}
            selectedDraftArray={assigneesDraft}
            setSelectedDraftArray={setAssigneesDraft}
            selectedArray={selectedTeamMembers}
            setSelectedArray={handleTeamMembersChange}
            dataKey="firstName"
            secondaryDataKey="lastName"
            dataIdKey={'merchantId'}
            buttonPrimaryText="Team"
            buttonSecondaryText={getTextForFilter(
              getMerchantsResponse || [],
              selectedTeamMembers,
              'merchantId',
              'firstName'
            )}
            filterPlaceholderText="Filter by team"
            searchPlaceHolderText="Search team members"
            allowSearch={true}
            filterPlaceholderStyle="font-semibold"
            searchInputStyle="placeholder:text-primaryText"
          />
        )}
        <DropdownWithSearch
          searchFilterId="jobs-tags-search-field"
          filterId="jobs-tags-filter"
          data={tagsOptions}
          searchText={tagsSearchString}
          setSearchText={setTagsSearchString}
          selectedDraftArray={selectedTagsDraft}
          setSelectedDraftArray={setSelectedTagsDraft}
          selectedArray={selectedTags}
          setSelectedArray={handleTagsChange}
          dataKey="name"
          dataIdKey={'id'}
          buttonPrimaryText="Tags"
          buttonSecondaryText={getTextForFilter(tagsOptions, selectedTags, 'id', 'name')}
          filterPlaceholderText="Filter by tags"
          searchPlaceHolderText="Search tag names"
          allowSearch={true}
          filterPlaceholderStyle="font-semibold"
          searchInputStyle="placeholder:text-primaryText"
        />
        {selectedMerchants.length > 0 && (
          <DropdownWithSearch
            searchFilterId="jobs-createdby-search-field"
            filterId="jobs-createdby-filter"
            data={getMerchantsResponse || []}
            searchText={createdBySearchString}
            setSearchText={setCreatedBySearchString}
            selectedDraftArray={selectedMerchantsDraft}
            setSelectedDraftArray={setSelectedMerchantsDraft}
            selectedArray={selectedMerchants}
            setSelectedArray={handleMerchantsChange}
            dataKey="firstName"
            secondaryDataKey="lastName"
            dataIdKey={'merchantId'}
            buttonPrimaryText="Created by"
            buttonSecondaryText={getTextForFilter(
              getMerchantsResponse || [],
              selectedMerchants,
              'merchantId',
              'firstName'
            )}
            filterPlaceholderText="Filter by Merchants"
            searchPlaceHolderText="Search merchant names"
            allowSearch={true}
            filterPlaceholderStyle="font-semibold"
            searchInputStyle="placeholder:text-primaryText"
          />
        )}
        {viewJobSource && leadSources.length > 0 && (
          <DropdownWithSearch
            filterId="jobs-lead-source-filter"
            data={JobLeadSourceTypes}
            selectedDraftArray={leadSourcesDraft}
            setSelectedDraftArray={setLeadSourcesDraft}
            selectedArray={leadSources}
            setSelectedArray={handleLeadSourcesChange}
            dataKey="label"
            dataIdKey="value"
            buttonPrimaryText="Lead source"
            buttonSecondaryText={getTextForFilter(JobLeadSourceTypes, leadSources, 'value', 'label')}
            filterPlaceholderText="Filter by lead source"
            allowSearch={false}
            filterPlaceholderStyle="font-semibold"
            searchInputStyle="placeholder:text-primaryText"
          />
        )}
        {selectedCustomers.length > 0 && (
          <DropdownWithSearch
            searchFilterId="jobs-customer-name-search-field"
            filterId="jobs-customer-filter"
            data={getCustomersResponse?.customers || []}
            searchText={customerSearchString}
            setSearchText={setCustomerSearchString}
            selectedDraftArray={selectedCustomersDraft}
            setSelectedDraftArray={setSelectedCustomersDraft}
            selectedArray={selectedCustomers}
            setSelectedArray={handleCustomersChange}
            dataKey="name"
            secondaryDataKey="lastName"
            dataIdKey={'ID'}
            buttonPrimaryText="Customers"
            buttonSecondaryText={getTextForFilter(
              getCustomersResponse?.customers || [],
              selectedCustomers,
              'ID',
              'name'
            )}
            filterPlaceholderText="Filter by customers"
            searchPlaceHolderText="Search customer names"
            allowSearch={true}
            filterPlaceholderStyle="font-semibold"
            searchInputStyle="placeholder:text-primaryText"
          />
        )}
        {amountFilter.filterOperation !== AmountFilterOperation.ALL && (
          <CustomAmountFilter amountFilter={amountFilter} setAmountFilter={handleAmountFilterChange} />
        )}
        {externalCompanies.length > 0 && (viewJobSource || editJobSource) && (
          <DropdownWithSearch
            filterId="jobs-external-company-filter"
            data={externalCompanyOptions}
            selectedDraftArray={externalCompaniesDraft}
            setSelectedDraftArray={setExternalCompaniesDraft}
            selectedArray={externalCompanies}
            setSelectedArray={handleExternalCompaniesChange}
            dataKey="label"
            dataIdKey="value"
            buttonPrimaryText="External company"
            buttonSecondaryText={getTextForFilter(externalCompanyOptions, externalCompanies, 'value', 'label')}
            filterPlaceholderText="Filter by external company"
            allowSearch={false}
            filterPlaceholderStyle="font-semibold"
            searchInputStyle="placeholder:text-primaryText"
          />
        )}
        {selectedServiceAreas.length > 0 && (
          <DropdownWithSearch
            filterId="jobs-service-area-filter"
            data={serviceAreaOptions}
            selectedDraftArray={serviceAreasDraft}
            setSelectedDraftArray={setServiceAreasDraft}
            selectedArray={selectedServiceAreas}
            setSelectedArray={handleServiceAreasChange}
            dataKey="name"
            dataIdKey="id"
            buttonPrimaryText="Service area"
            buttonSecondaryText={getTextForFilter(serviceAreaOptions, selectedServiceAreas, 'id', 'name')}
            filterPlaceholderText="Filter by service area"
            allowSearch={false}
            filterPlaceholderStyle="font-semibold"
            searchInputStyle="placeholder:text-primaryText"
          />
        )}
        <JobMoreFilter
          searchFilterId="jobs-customer-name-search-field"
          filterId="jobs-customer-filter"
          data={getCustomersResponse?.customers || []}
          searchText={customerSearchString}
          setSearchText={setCustomerSearchString}
          selectedDraftArray={selectedCustomersDraft}
          setSelectedDraftArray={setSelectedCustomersDraft}
          setSelectedArray={handleCustomersChange}
          selectedArray={selectedCustomers}
          dataKey="name"
          secondaryDataKey="lastName"
          dataIdKey={'ID'}
          filterPlaceholderText="Filter by customers"
          searchPlaceHolderText="Search customer names"
          allowSearch={true}
          filterPlaceholderStyle="font-semibold"
          searchInputStyle="placeholder:text-primaryText"
          amountFilter={amountFilter}
          setAmountFilter={handleAmountFilterChange}
          externalCompanies={externalCompanies}
          showJobSourceFilter={viewJobSource || editJobSource}
          setExternalCompanies={handleExternalCompaniesChange}
          externalCompaniesDraft={externalCompaniesDraft}
          setExternalCompaniesDraft={setExternalCompaniesDraft}
          externalCompanyOptions={externalCompanyOptions}
          createdByData={getMerchantsResponse || []}
          createdBySearchText={createdBySearchString}
          setCreatedBySearchText={setCreatedBySearchString}
          createdByDraft={selectedMerchantsDraft}
          setCreatedByDraft={setSelectedMerchantsDraft}
          createdBySelected={selectedMerchants}
          setCreatedBySelected={handleMerchantsChange}
          createdByDataKey="firstName"
          createdBySecondaryDataKey="lastName"
          createdByDataIdKey="merchantId"
          leadSourceData={JobLeadSourceTypes}
          leadSourceDraft={leadSourcesDraft}
          setLeadSourceDraft={setLeadSourcesDraft}
          leadSourceSelected={leadSources}
          setLeadSourceSelected={handleLeadSourcesChange}
          leadSourceDataKey="label"
          leadSourceDataIdKey="value"
          serviceAreaOptions={serviceAreaOptions}
          serviceAreaDraft={serviceAreasDraft}
          setServiceAreaDraft={setServiceAreasDraft}
          selectedServiceAreas={selectedServiceAreas}
          setSelectedServiceAreas={handleServiceAreasChange}
          serviceAreaSearch={serviceAreaSearch}
          setServiceAreaSearch={setServiceAreaSearch}
        />
      </div>
    </>
  );
};

export default JobsFilter;
